export type AppInsightsLike = {
	trackEvent: (event: { name: string; properties?: Record<string, string>; measurements?: Record<string, number> }) => void;
};

export type ClickAnalyticsLike = {
	trackPageAction: (
		pageAction: { name: string; uri?: string; actionType?: string; content?: string },
		customProperties?: Record<string, string>
	) => void;
};

/**
 * Creates a short random key suitable for telemetry session IDs and interaction tracking handles.
 * The key combines a base-36 timestamp with a random suffix for compact uniqueness.
 * Optionally prefixed for readability (e.g. "filter" → "filter-lp0a1b2c-d3e4f5g6").
 */
export function createTelemetryKey(prefix?: string): string {
	const key = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
	return prefix ? `${prefix}-${key}` : key;
}

/**
 * Emits a custom event via AppInsights if the provided instance is available.
 */
export function trackTelemetryEvent(
	ai: AppInsightsLike | undefined | null,
	name: string,
	properties: Record<string, string>,
	measurements: Record<string, number>
): void {
	if (ai)
		ai.trackEvent({ name, properties, measurements });
}

/**
 * Emits a click event through the Click Analytics plugin for elements its auto-capture cannot see —
 * the plugin only auto-captures clicks that bubble to an anchor, button, area, or input element,
 * so tags on other elements (e.g. table rows) must be reported manually. The emitted event matches
 * the auto-captured schema: name from `data-cf-id`, remaining `data-cf-*` attributes in `content`.
 *
 * String values in `customProperties` become flat custom dimensions on the emitted event. Numbers
 * would be routed to `measurements` instead, so pass every value as a string.
 */
export function trackClickEvent(
	clickAnalytics: ClickAnalyticsLike | undefined | null,
	dataAttrs: Record<string, string>,
	customProperties?: Record<string, string>
): void {
	const name = dataAttrs?.['data-cf-id'];
	if (!clickAnalytics || !name)
		return;
	const content: Record<string, string> = {};
	for (const [attr, value] of Object.entries(dataAttrs)) {
		if (attr !== 'data-cf-id' && attr.startsWith('data-cf-'))
			content[attr.replace('data-cf-', '')] = value;
	}
	const pageAction = {
		name,
		uri: window.location.href,
		actionType: 'CL',
		content: `[${JSON.stringify(content)}]`
	};
	if (customProperties)
		clickAnalytics.trackPageAction(pageAction, customProperties);
	else
		clickAnalytics.trackPageAction(pageAction);
}
